import { Helmet } from 'react-helmet-async';
import { useAutoScaler } from '../../hooks/useAutoScaler';
import '../../styles/aida-base.css';
import '../../styles/aida-responsive.css';

const GOOGLE_DOC_EMBED_URL =
  'https://docs.google.com/document/d/e/2PACX-1vS3TfbW9L5raKFB6FTKDZumK6kg8n9FhB007LCyXM8MvtphGRSCFm1K6BFEyA62LA/pub?embedded=true';

const GOOGLE_DOC_ID = '1kBEBd7pCi4jimxNIm2ZSmsdM14mUXQBP';
const DOWNLOAD_PDF_URL = `https://docs.google.com/document/d/${GOOGLE_DOC_ID}/export?format=pdf`;
const DOWNLOAD_DOCX_URL = `https://docs.google.com/document/d/${GOOGLE_DOC_ID}/export?format=docx`;

const buttonBase: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '10px 28px',
  borderRadius: '30px',
  fontFamily: 'Inter, sans-serif',
  fontWeight: 600,
  fontSize: '14px',
  textDecoration: 'none',
  cursor: 'pointer',
  lineHeight: '22px',
  whiteSpace: 'nowrap',
};

const PURPLE_NEBULA = {
  background: [
    'linear-gradient(to bottom, rgba(0,0,0,1) 0%, rgba(0,0,0,0.85) 10%, rgba(0,0,0,0.2) 25%, rgba(0,0,0,0.0) 40%, rgba(0,0,0,0.0) 60%, rgba(0,0,0,0.2) 75%, rgba(0,0,0,0.85) 90%, rgba(0,0,0,1) 100%)',
    'linear-gradient(90deg, rgba(120,40,255,0.55), rgba(180,60,255,0.55))',
    'radial-gradient(1400px at 50% 40%, rgba(255,255,255,0.03), rgba(60,0,90,0) 75%)',
    'radial-gradient(1000px at 15% 35%, rgba(140,40,255,0.40), rgba(40,0,70,0) 75%)',
    'radial-gradient(900px at 55% 60%, rgba(160,50,255,0.30), rgba(35,0,60,0) 80%)',
    'radial-gradient(1000px at 85% 70%, rgba(200,70,255,0.40), rgba(45,0,70,0) 80%)',
    'radial-gradient(circle at 50% 50%, rgba(255,255,255,0.02) 0%, rgba(255,255,255,0.00) 60%)',
    'radial-gradient(circle at 50% 25%, #2a0050 0%, #1a0038 35%, #000000 75%, #000000 100%)',
  ].join(', '),
  backgroundBlendMode: 'normal, soft-light, overlay, screen, screen, screen, soft-light, multiply',
};

const titleStyle: React.CSSProperties = {
  fontFamily: 'Inter, sans-serif',
  fontWeight: 900,
  fontStyle: 'italic',
  letterSpacing: '-0.3px',
  color: '#fff',
  margin: 0,
  fontVariationSettings: "'wght' 900",
};

export const ServicesAgreement = () => {
  const { isContentReady } = useAutoScaler();

  return (
    <>
      <Helmet>
        <title>Services Agreement | Playcast</title>
        <meta name="description" content="Playcast Services Agreement — terms governing the use of Playcast services." />
      </Helmet>

      <div
        className="w-full"
        style={{
          backgroundColor: 'black',
          margin: 0,
          padding: 0,
          width: '100%',
          overflow: 'hidden',
        }}
      >
        <div style={{ paddingTop: '60px', display: 'flex', justifyContent: 'center', margin: 0, padding: 0 }}>
          <div
            id="auto-scaler"
            style={{
              width: '1024px',
              transformOrigin: 'top center',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              margin: 0,
              padding: 0,
              overflow: 'visible',
              opacity: isContentReady ? 1 : 0,
              transition: 'opacity 0.3s ease-in-out',
            }}
          >
            <div style={{ width: '100%', ...PURPLE_NEBULA, minHeight: '100vh' }}>
              <div className="px-4 pt-[72px] pb-16 lg:px-36 lg:pt-[120px] lg:pb-60" style={{ width: '100%' }}>

                {/* Desktop header — title and buttons side by side */}
                <div className="hidden lg:flex" style={{ alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
                  <h1 style={{ ...titleStyle, fontSize: '28px', lineHeight: '36px' }}>
                    Playcast Services Agreement
                  </h1>
                  <div style={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
                    <a
                      href={DOWNLOAD_PDF_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="aida-button aida-button-outline-fill"
                      style={{ ...buttonBase, fontSize: '12px', padding: '7px 18px' }}
                    >
                      <span className="text">Download PDF</span>
                    </a>
                    <a
                      href={DOWNLOAD_DOCX_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="aida-button"
                      style={{ ...buttonBase, fontSize: '12px', padding: '7px 18px', background: '#FBBF24', color: '#161423' }}
                    >
                      <span className="text">Download DOCX</span>
                    </a>
                  </div>
                </div>

                {/* Mobile header — stacked title then buttons */}
                <div className="flex flex-col items-center lg:hidden" style={{ gap: '16px', marginBottom: '20px', padding: '0 8px' }}>
                  <h1 style={{ ...titleStyle, fontSize: '48px', lineHeight: '54px', textAlign: 'center' }}>
                    Playcast<br />Services<br />Agreement
                  </h1>
                  <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
                    <a
                      href={DOWNLOAD_PDF_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="aida-button aida-button-outline-fill"
                      style={{ ...buttonBase, fontSize: '13px', padding: '8px 20px' }}
                    >
                      <span className="text">Download PDF</span>
                    </a>
                    <a
                      href={DOWNLOAD_DOCX_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="aida-button"
                      style={{ ...buttonBase, fontSize: '13px', padding: '8px 20px', background: '#FBBF24', color: '#161423' }}
                    >
                      <span className="text">Download DOCX</span>
                    </a>
                  </div>
                </div>

                <iframe
                  src={GOOGLE_DOC_EMBED_URL}
                  title="Playcast Services Agreement"
                  style={{
                    width: '100%',
                    height: 'calc(100vh - 200px)',
                    border: 'none',
                    borderRadius: '8px',
                    background: '#fff',
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
